import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  FileText, 
  Calendar,
  ArrowUpRight,
  PieChart as PieIcon,
  BedDouble,
  Activity
} from "lucide-react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import ResourcePage from "../../components/ResourcePage";
import { api } from "../../axios/api";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const Main = () => {
  const { user, empresa } = useSelector((state) => state.user);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [filtros, setFiltros] = useState({
    inicio: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    fim: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0]
  });

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.post("/dashboard", {
        empresa_id: empresa?.id || user?.empresa_id,
        inicio: filtros.inicio,
        fim: filtros.fim
      });
      setDashboardData(response.data?.data || response.data);
    } catch (err) {
      console.error("Erro ao carregar o dashboard:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (empresa?.id || user?.empresa_id) {
      fetchDashboard();
    }
  }, [empresa?.id, user?.empresa_id, filtros]);

  const handleDateChange = (e) => {
    setFiltros({ ...filtros, [e.target.name]: e.target.value });
  };

  const financeiro = dashboardData?.financeiro || { total_receitas: 0, total_despesas: 0, lucro_liquido: 0, comparativo_receita_anterior: 0, crescimento_receita_pct: 0 };
  const quartos = dashboardData?.quartos || { total: 0, ocupados: 0, disponiveis: 0 };
  const documentos = dashboardData?.documentos || { validados: 0, anulados: 0, lista_recente: [] };
  const servicosPopulares = dashboardData?.servicos_populares || [];
  const clientesRecorrentes = dashboardData?.clientes_recorrentes || [];
  const graficoAnual = dashboardData?.grafico_anual || { receitas: Array(12).fill(0), despesas: Array(12).fill(0) };

  const anualChartData = {
    labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
    datasets: [
      {
        label: 'Receitas (Kz)',
        data: graficoAnual.receitas,
        backgroundColor: 'rgba(25, 135, 84, 0.7)',
        borderRadius: 4,
      },
      {
        label: 'Despesas (Kz)',
        data: graficoAnual.despesas,
        backgroundColor: 'rgba(220, 53, 69, 0.7)',
        borderRadius: 4,
      },
    ],
  };

  const doughnutChartData = {
    labels: ['Validados', 'Anulados'],
    datasets: [
      {
        data: [documentos.validados, documentos.anulados],
        backgroundColor: ['rgba(25, 135, 84, 0.8)', 'rgba(220, 53, 69, 0.8)'],
        borderColor: ['#fff', '#fff'],
        borderWidth: 2,
      },
    ],
  };

  return (
    <ResourcePage title="Visão Geral & Dashboard Analítico" breadcrumb={["Facturize", "Hotelaria"]}>
      {/* Filtro de Período */}
      <div className="card bg-white border-0 shadow-sm mb-4">
        <div className="card-body py-3">
          <div className="row align-items-center g-3">
            <div className="col-md-auto">
              <span className="fw-semibold text-muted d-flex align-items-center gap-1">
                <Calendar size={18} /> Filtrar Período:
              </span>
            </div>
            <div className="col-md-3">
              <input 
                type="date" 
                className="form-control form-control-sm" 
                name="inicio" 
                value={filtros.inicio} 
                onChange={handleDateChange} 
              />
            </div>
            <div className="col-md-3">
              <input 
                type="date" 
                className="form-control form-control-sm" 
                name="fim" 
                value={filtros.fim} 
                onChange={handleDateChange} 
              />
            </div>
            <div className="col-md-auto">
              <button 
                className="btn btn-primary btn-sm px-4" 
                onClick={fetchDashboard}
                disabled={loading}
              >
                {loading ? "A carregar..." : "Aplicar Filtro"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Cartões Estatísticos Principais */}
      <div className="row">
        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card bg-white border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="text-success bg-success-subtle p-2 rounded d-inline-flex mb-3"><TrendingUp size={22} /></div>
              <p className="text-muted mb-1 small">Receitas (Período)</p>
              <h5 className="mb-2 text-success fw-bold">{Number(financeiro.total_receitas).toLocaleString()} Kz</h5>
              <span className={`badge ${financeiro.crescimento_receita_pct >= 0 ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}`}>
                {financeiro.crescimento_receita_pct >= 0 ? '+' : ''}{financeiro.crescimento_receita_pct}% vs mês anterior
              </span>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card bg-white border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="text-danger bg-danger-subtle p-2 rounded d-inline-flex mb-3"><TrendingDown size={22} /></div>
              <p className="text-muted mb-1 small">Despesas (Período)</p>
              <h5 className="mb-2 text-danger fw-bold">{Number(financeiro.total_despesas).toLocaleString()} Kz</h5>
              <span className="text-muted small">Mês anterior: {Number(financeiro.comparativo_receita_anterior ? financeiro.total_despesas : 0).toLocaleString()} Kz</span>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card bg-white border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="text-primary bg-primary-subtle p-2 rounded d-inline-flex mb-3"><DollarSign size={22} /></div>
              <p className="text-muted mb-1 small">Lucro Líquido</p>
              <h5 className="mb-2 text-primary fw-bold">{Number(financeiro.lucro_liquido).toLocaleString()} Kz</h5>
              <span className="text-muted small">Balanço do período</span>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card bg-white border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="text-info bg-info-subtle p-2 rounded d-inline-flex mb-3"><BedDouble size={22} /></div>
              <p className="text-muted mb-1 small">Quartos (Ocupados / Total)</p>
              <h5 className="mb-2 fw-bold">{quartos.ocupados} / {quartos.total}</h5>
              <span className="badge bg-info-subtle text-info">{quartos.disponiveis} disponíveis</span>
            </div>
          </div>
        </div>
      </div>

      {/* Gráfico Anual de Produtividade (12 Meses) */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card bg-white border-0 shadow-sm">
            <div className="card-body">
              <h4 className="card-title mb-3 fs-5 d-flex align-items-center gap-2">
                <Activity size={20} className="text-primary" /> Produtividade Anual (Evolução de 12 Meses)
              </h4>
              <div style={{ height: "320px" }}>
                <Bar data={anualChartData} options={{ responsive: true, maintainAspectRatio: false }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Serviços Populares & Clientes Recorrentes */}
      <div className="row mb-4">
        <div className="col-lg-6 mb-4 mb-lg-0">
          <div className="card bg-white border-0 shadow-sm h-100">
            <div className="card-body">
              <h4 className="card-title mb-3 fs-5">Serviços Mais Solicitados</h4>
              <div className="table-responsive">
                <table className="table table-centered table-hover mb-0 align-middle">
                  <thead>
                    <tr className="text-muted fs-7">
                      <th>Serviço / Produto</th>
                      <th className="text-end">Quantidade Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {servicosPopulares.length > 0 ? (
                      servicosPopulares.map((serv, index) => (
                        <tr key={index}>
                          <td>{serv.nome}</td>
                          <td className="text-end fw-bold">{serv.quantidade}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="2" className="text-center text-muted py-3">Sem dados suficientes no período.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="card bg-white border-0 shadow-sm h-100">
            <div className="card-body">
              <h4 className="card-title mb-3 fs-5">Clientes Recorrentes</h4>
              <div className="table-responsive">
                <table className="table table-centered table-hover mb-0 align-middle">
                  <thead>
                    <tr className="text-muted fs-7">
                      <th>Nome do Cliente</th>
                      <th className="text-end">Total de Facturas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientesRecorrentes.length > 0 ? (
                      clientesRecorrentes.map((cli, index) => (
                        <tr key={index}>
                          <td>{cli.nome}</td>
                          <td className="text-end fw-bold text-success">{cli.total_facturas} documentos</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="2" className="text-center text-muted py-3">Nenhum cliente recorrente registado.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Listas Recentes e Gráfico de Rosca */}
      <div className="row">
        <div className="col-lg-4 mb-4">
          <div className="card bg-white border-0 shadow-sm h-100">
            <div className="card-body">
              <h4 className="card-title mb-3 fs-5 d-flex align-items-center gap-2">
                <PieIcon size={20} className="text-primary" /> Estado dos Documentos
              </h4>
              <div style={{ height: "240px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Doughnut data={doughnutChartData} options={{ responsive: true, maintainAspectRatio: false }} />
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-8 mb-4">
          <div className="card bg-white border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h4 className="card-title mb-0 fs-5">Documentos Recentes</h4>
                <Link to="/documentos" className="text-decoration-none small d-flex align-items-center gap-1">
                  Ver todos <ArrowUpRight size={14} />
                </Link>
              </div>
              <div className="table-responsive">
                <table className="table table-centered table-hover mb-0 align-middle">
                  <thead>
                    <tr className="text-muted fs-7">
                      <th>Tipo / Nº</th>
                      <th>Data</th>
                      <th>Total</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documentos.lista_recente?.length > 0 ? (
                      documentos.lista_recente.map((doc) => (
                        <tr key={doc.id}>
                          <td><span className="fw-semibold">{doc.tipo}</span> {doc.numero}</td>
                          <td>{doc.data_emissao}</td>
                          <td>{Number(doc.valor_pago || 0).toLocaleString()} Kz</td>
                          <td>
                            <span className={`badge ${doc.estado === "Validado" ? "bg-success-subtle text-success" : "bg-warning-subtle text-warning"}`}>
                              {doc.estado}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center text-muted py-3">Nenhum documento encontrado.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ResourcePage>
  );
};

export default Main;
